import React, { useState, useEffect } from 'react';
import { Card, CardBody, Button } from 'reactstrap';
import { Link, Prompt } from 'react-router-dom';
import moment from 'moment';
import TableIcon from 'mdi-react/TableIcon';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import StadiumIcon from 'mdi-react/StadiumIcon';
import SitemapIcon from 'mdi-react/SitemapIcon';
import CalendarIcon from 'mdi-react/CalendarIcon';
import TicketIcon from 'mdi-react/TicketIcon';
import DELIMITER from 'mdi-react/FormatPageBreakIcon';
import LINK from 'mdi-react/LinkVariantIcon';
import WalletMembershipIcon from 'mdi-react/WalletMembershipIcon';
import GavelIcon from 'mdi-react/GavelIcon';
import YoutubeIcon from 'mdi-react/YoutubeIcon';
import BellAlertIcon from 'mdi-react/BellAlertIcon';
import ImageAreaIcon from 'mdi-react/ImageAreaIcon';
import BasketIcon from 'mdi-react/BasketIcon';
import BrightnessPercentIcon from 'mdi-react/BrightnessPercentIcon';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import DragVerticalIcon from 'mdi-react/DragVerticalIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import AlertOutlineIcon from 'mdi-react/AlertOutlineIcon';
import ClubheartIcon from '../../shared/components/custom/ClubheartIcon';
import VideoIcon from 'mdi-react/VideoIcon';
import SoccerFieldIcon from 'mdi-react/SoccerFieldIcon';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import Loading from '../../shared/components/custom/Loading';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import componentName from './componentName';
import getClubWebsite from '../../util/clubWebsites';
import useConfig from '../../util/useConfig';
import CustomWebsiteComponent from './CustomWebsiteComponent';
import SectionLabelComponent from './SectionLabelComponent';
import ExternalLinkWebsiteComponent from './ExternalLinkWebsiteComponent';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import NavTab from '../../shared/components/custom/NavTab';
import {
  getReadableFormat,
  getReadableSize,
} from '../Spotlight/spotlightUtils';

const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';
// Tiles users see right now: published and not expired (as on the tile list)
const isTileLive = (tile) =>
  moment(tile.publicationDate).isSameOrBefore(moment()) &&
  (!tile.expirationDate ||
    tile.expirationDate === UNLIMITED_DATE ||
    moment(tile.expirationDate).isAfter(moment()));
const liveTileCount = (tiles = []) => tiles.filter(isTileLive).length;

const getIconComponent = (type) => {
  switch (true) {
    case type.split('-')[0] === 'spotlight':
      return ImageAreaIcon;
    case type === 'stats':
    case type === 'standings':
    case type === 'playerStats':
      return TableIcon;
    case type === 'news':
    case type === 'featuredNews':
    case type === 'specific-news':
      return NewspaperIcon;
    case type === 'questionaires':
      return NewspaperIcon;
    case type === 'players':
    case type === 'teams':
      return AccountGroupIcon;
    case type === 'tierBanner':
    case type === 'tiers':
      return WalletMembershipIcon;
    case type === 'matchBanner':
    case type === 'matchCenter':
    case type === 'matchcenter':
    case type === 'matches':
    case type === 'upcomingMatches':
      return StadiumIcon;
    case type === 'shop':
      return BasketIcon;
    case type === 'sponsors':
    case type === 'companies':
    case type === 'specific-companies':
      return SitemapIcon;
    case type === 'events':
      return CalendarIcon;
    case type === 'lottery':
    case type === 'seasonBanner':
    case type === 'tickets':
    case type === 'clubmateLottery':
      return TicketIcon;
    case type === 'offers':
      return BrightnessPercentIcon;
    case type === 'auctions':
      return GavelIcon;
    case type === 'youTubeFeed':
    case type === 'featuredVideos':
    case type === 'shortVideos':
      return YoutubeIcon;
    case type === 'custom':
      return NewspaperIcon;
    case type === 'seasonMatchNotificationsBanner':
      return BellAlertIcon;
    case type === 'termsBanner':
      return BellAlertIcon;
    case type === 'NTFGoalFeed':
      return BellAlertIcon;
    case type === 'externalLink':
    case type === 'external-link':
    case type === 'specific-external-link':
      return LINK;
    case type === 'sectionLabel':
    case type === 'section-label':
      return DELIMITER;
    case type.toLowerCase().startsWith('forzasys'):
      return VideoIcon;
    case type.toLowerCase().startsWith('fogis'):
      return SoccerFieldIcon;
    case type === 'goal-sponsor':
    case type === 'goal-club-pitch':
    case type === 'goal-sponsor-sales':
      return ClubheartIcon;
    default:
      return ImageOutlineIcon;
  }
};

const getPremium = (type) => {
  switch (true) {
    case type === 'youTubeFeed':
    case type === 'custom':
      return true;
    default:
      return false;
  }
};

const getFeatures = (features, name) => {
  if (features?.includes(name)) {
    return true;
  }
  return false;
};

// Unlike getPremium's items (shown but dimmed as an upsell), these are
// integrations that only make sense for clubs that actually use them - so
// they're hidden entirely rather than shown-disabled when the flag is off,
// same as SidebarContent gates a whole nav category on `features?.includes`.
// Every id the backend returns for these integrations (forzasysStats,
// ForzaSysTopStats, fogisClub, ...) shares one flag per integration, hence
// the prefix match rather than an exact-id list.
const isFeatureVisible = (type, features) => {
  const normalized = type.toLowerCase();
  if (normalized.startsWith('forzasys')) {
    return features?.includes('forzasys');
  }
  if (normalized.startsWith('fogis')) {
    return features?.includes('fogis');
  }
  if (normalized === 'externallivescore') {
    return features?.includes('externalLiveScore');
  }
  if (normalized === 'ntfgoalfeed') {
    return features?.includes('forzasys');
  }
  if (
    normalized === 'goal-sponsor' ||
    normalized === 'goal-club-pitch' ||
    normalized === 'goal-sponsor-sales' ||
    normalized === 'testimonials' ||
    normalized === 'club-sales' ||
    normalized === 'how-it-works' ||
    normalized === 'club-logos' ||
    normalized === 'popular-fundraisers' ||
    normalized === 'profit-calculator'
  ) {
    return features?.includes('score');
  }
  return true;
};

// The four areas of the website, in tab order
// The four areas (their saved keys), in tab order: what's worked on most
// first. Names and descriptions: appLayout.website.* (the app layout page
// has its own bottomMenu text)
const AREAS = ['mainScreen', 'middleMenu', 'bottomMenu', 'topMenu'];

// Groups for the Available list, in display order; ids not listed go under
// "Other"
const GROUPS = [
  {
    key: 'news',
    ids: ['news', 'featuredNews', 'exlusiveNews', 'specific-news'],
  },
  {
    key: 'matches',
    ids: [
      'matchBanner',
      'upcomingMatches',
      'standings',
      'matches',
      'teams',
      'fogis',
    ],
  },
  { key: 'video', ids: ['featuredVideos', 'shortVideos', 'forzasys'] },
  {
    key: 'promotion',
    ids: [
      'specific-spotlight',
      'appDownloadBanner',
      'clubmateLottery',
      'lotteries',
      'tiers',
      'companies',
      'specific-companies',
    ],
  },
  {
    key: 'score',
    ids: [
      'goal-sponsor',
      'goal-club-pitch',
      'goal-sponsor-sales',
      'testimonials',
      'club-sales',
      'how-it-works',
      'club-logos',
      'popular-fundraisers',
      'profit-calculator',
    ],
  },
  { key: 'links', ids: ['section-label', 'specific-external-link'] },
];
const groupOf = (id) =>
  GROUPS.find((group) => group.ids.includes(id))?.key || 'other';
const groupOrder = (id) => {
  const index = GROUPS.findIndex((group) => group.ids.includes(id));
  const group = GROUPS[index];
  return index === -1
    ? GROUPS.length * 100
    : index * 100 + group.ids.indexOf(id);
};

// Items placed more than once, with content chosen in a modal
const hasContent = (id) => id.includes('specific-') || id === 'section-label';

// Placed items get their own id: two of the same article would otherwise
// look alike
let uidCounter = 0;
const newUid = () => {
  uidCounter += 1;
  return `wl-${uidCounter}`;
};

// What a placed item shows as: its chosen content, else its name
const itemTitle = (item) =>
  item.id === 'section-label' || hasContent(item.id)
    ? `${item.label ?? ''}` || item.name
    : item.name;

// How club-website renders the main screen (src/app/[locale]/(home)/page.tsx):
// ids with a dash of their own are whole, the rest are "prefix-type"
const SITE_WHOLE_IDS = [
  'goal-sponsor',
  'club-sales',
  'goal-club-pitch',
  'goal-sponsor-sales',
  'how-it-works',
  'club-logos',
  'popular-fundraisers',
  'profit-calculator',
];
// Widget types the website has; anything else renders nothing there
const SITE_WIDGETS = [
  ...SITE_WHOLE_IDS,
  'testimonials',
  'spotlight',
  'news',
  'exlusiveNews',
  'clubmateLottery',
  'appDownloadBanner',
  'matchBanner',
  'standings',
  'upcomingMatches',
  'featuredNews',
  'featuredVideos',
  'shortVideos',
];
// Full-width bands; the others sit in the site's 1280 px column
const SITE_FULL_WIDTH = [...SITE_WHOLE_IDS, 'testimonials', 'featuredNews'];
const siteWidgetType = (id) => {
  const [type, specificType] = `${id}`.split('-');
  return SITE_WHOLE_IDS.includes(id) ? id : specificType || type;
};
const shownOnSite = (id) => SITE_WIDGETS.includes(siteWidgetType(id));

// Rough heights of the front page widgets in the preview (px)
const PREVIEW_HEIGHTS = {
  spotlight: 64,
  featuredNews: 92,
  featuredVideos: 60,
  matchBanner: 40,
  news: 56,
  exlusiveNews: 56,
  standings: 56,
  upcomingMatches: 44,
};

const WebsiteLayoutPage = () => {
  const config = useConfig();

  const [tab, setTab] = useState('1');
  // Changes not yet saved, in any of the four areas
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [query, setQuery] = useState('');
  // The list of things to check before saving (opened from the save bar)
  const [showChecks, setShowChecks] = useState(false);

  const features = config?.featureFlags;
  const [availableEntities, setAvailableEntities] = useState({
    news: [],
    companies: [],
  });
  // Spotlight groups by id, for the details shown on their rows
  const [spotlightGroups, setSpotlightGroups] = useState({});
  const [topMenu, setTopMenu] = useState({
    Col0: {
      name: 'Components',
      items: [],
    },
  });
  const [middleMenu, setMiddleMenu] = useState({
    Col0: {
      name: 'Components',
      items: [],
    },
  });
  const [bottomMenu, setBottomMenu] = useState({
    Col0: {
      name: 'Components',
      items: [],
    },
  });
  const [mainScreen, setMainScreen] = useState({
    Col0: {
      name: 'Components',
      items: [],
    },
  });
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [showModal, setShowModal] = useState(null);

  const p = partial('appLayout');
  const w = partial('appLayout.website');
  const s = partial('shared');

  const mapLayoutToPayload = (layout) => {
    return layout.Col1.items.map((item, idx) => ({
      id: item.id,
      value: item.value || item.id,
      label: item.label || null,
      order: idx + 1,
    }));
  };
  const handleSubmit = async (e) => {
    e?.preventDefault();

    const payload = {
      topMenu: mapLayoutToPayload(topMenu),
      middleMenu: mapLayoutToPayload(middleMenu),
      bottomMenu: mapLayoutToPayload(bottomMenu),
      mainScreen: mapLayoutToPayload(mainScreen),
    };
    setLoadingComponents(true);
    try {
      await Api.app_layout.website.create(config.id, payload);
      toast.success(s('Toast.successfullEdit'));
      setDirty(false);
      setSaved(true);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    }
    setLoadingComponents(false);
  };
  const fetchEntities = async () => {
    const [news, companies, spotlight] = await Promise.all([
      Api.news
        .getNews(
          { page: 1, take: 100 },
          { orderBy: 'publicationDate', direction: 'DESC' }
        )
        .then(({ data }) =>
          data.map((item) => ({ label: item.title, value: item.id }))
        ),
      Api.companies
        .getCompaniesForClub()
        .then((data) =>
          data.map((item) => ({ label: item.name, value: item.id }))
        ),
      Api.spotlight
        .getGroups(
          { page: 1, take: 100 },
          { order: 'createdAt', direction: 'DESC' }
        )
        .then(({ data }) => {
          setSpotlightGroups(
            Object.fromEntries(data.map((group) => [group.id, group]))
          );
          return data.map((item) => ({ label: item.title, value: item.id }));
        }),
    ]);
    const constructed = {
      news,
      companies,
      spotlight,
    };
    setAvailableEntities(constructed);
    return constructed;
  };
  const mapDbEntities = (dbEntities, storedEntities) => {
    const mapped = [];
    for (let i = 0; i < dbEntities.length; i++) {
      const item = dbEntities[i];
      if (item.id.includes('specific-')) {
        if (item.id === 'specific-external-link') {
          mapped.push({
            uid: newUid(),
            id: item.id,
            name: componentName('externalLink'),
            value: item.value,
            label: item.label,
          });
        } else {
          const specificEntities =
            storedEntities[item.id.split('specific-')[1]];
          const foundName = specificEntities.find(
            (e) => e.value === item.value
          )?.label;
          mapped.push({
            uid: newUid(),
            id: item.id,
            name: componentName(item.id),
            value: item.value,
            label: item.label || foundName,
          });
        }
      } else if (item.id === 'section-label') {
        mapped.push({
          uid: newUid(),
          id: item.id,
          name: componentName(item.id),
          value: item.label ? item.value : i,
          label: item.label || i,
        });
      } else {
        mapped.push({
          uid: newUid(),
          id: item.id,
          name: componentName(item.id),
        });
      }
    }
    return mapped;
  };
  const getInitialData = async () => {
    setLoadingComponents(true);
    try {
      const getLayout = await Api.app_layout.website
        .all(config.id)
        .then((res) => {
          if (Object.keys(res.data).length === 0) {
            return {
              topMenu: [],
              middleMenu: [],
              bottomMenu: [],
              mainScreen: [],
            };
          }
          return {
            ...res.data,
            topMenu: res.data.topMenu || [],
            middleMenu: res.data.middleMenu || [],
            bottomMenu: res.data.bottomMenu || [],
            mainScreen: res.data.mainScreen || [],
          };
        });
      const results = await fetchEntities();
      const {
        topMenu: dbTopMenu,
        middleMenu: dbMiddleMenu,
        bottomMenu: dbBottomMenu,
        mainScreen: dbMainScreen,
      } = getLayout;
      const specificMenuComponents = [
        {
          id: 'specific-news',
          name: componentName('specific-news'),
          value: null,
        },
        {
          id: 'specific-companies',
          name: componentName('specific-companies'),
          value: null,
        },
        { id: 'news', name: componentName('news') },
        { id: 'companies', name: componentName('companies') },
        { id: 'teams', name: componentName('teams') },
        { id: 'matches', name: componentName('matches') },
        {
          id: 'specific-external-link',
          name: componentName('externalLink'),
          value: null,
        },
        { id: 'tiers', name: componentName('tiers') },
      ];
      // Only clubs with lotteries can link to them
      if (features?.includes('lottery')) {
        specificMenuComponents.push({
          id: 'lotteries',
          name: componentName('lotteries'),
        });
      }
      const specificMainScreenComponents = [
        {
          id: 'specific-spotlight',
          name: componentName('specific-spotlight'),
          value: null,
        },
        { id: 'news', name: componentName('news') },
        { id: 'matchBanner', name: componentName('matchBanner'), value: null },
        {
          id: 'featuredNews',
          name: componentName('featuredNews'),
          value: null,
        },
        { id: 'standings', name: componentName('standings'), value: null },
        {
          id: 'clubmateLottery',
          name: componentName('clubmateLottery'),
          value: null,
        },
        {
          id: 'appDownloadBanner',
          name: componentName('appDownloadBanner'),
          value: null,
        },
        {
          id: 'exlusiveNews',
          name: componentName('exlusiveNews'),
          value: null,
        },
        {
          id: 'upcomingMatches',
          name: componentName('upcomingMatches'),
          value: null,
        },
        { id: 'shortVideos', name: componentName('shortVideos'), value: null },
        {
          id: 'featuredVideos',
          name: componentName('featuredVideos'),
          value: null,
        },
        { id: 'forzasys', name: componentName('forzasys'), value: null },
        { id: 'fogis', name: componentName('fogis'), value: null },
        {
          id: 'goal-sponsor',
          name: componentName('goal-sponsor'),
          value: null,
        },
        {
          id: 'goal-club-pitch',
          name: componentName('goal-club-pitch'),
          value: null,
        },
        {
          id: 'goal-sponsor-sales',
          name: componentName('goal-sponsor-sales'),
          value: null,
        },
        {
          id: 'testimonials',
          name: componentName('testimonials'),
          value: null,
        },
        { id: 'club-sales', name: componentName('club-sales'), value: null },
        {
          id: 'how-it-works',
          name: componentName('how-it-works'),
          value: null,
        },
        { id: 'club-logos', name: componentName('club-logos'), value: null },
        {
          id: 'popular-fundraisers',
          name: componentName('popular-fundraisers'),
          value: null,
        },
        {
          id: 'profit-calculator',
          name: componentName('profit-calculator'),
          value: null,
        },
      ];
      const menuOptions = [...specificMenuComponents];
      const mainScreenOptions = [...specificMainScreenComponents].filter((e) =>
        isFeatureVisible(e.id, features)
      );
      const topMenuObject = {
        Col0: {
          name: 'Components',
          items: menuOptions.filter((e) =>
            e.id.includes('specific-')
              ? true
              : !dbTopMenu.some((h) => h.value === e.id)
          ),
        },
        Col1: {
          name: 'in-app',
          items: mapDbEntities(dbTopMenu, results),
        },
      };
      const middleMenuObject = {
        Col0: {
          name: 'Components',
          items: menuOptions.filter((e) =>
            e.id.includes('specific-')
              ? true
              : !dbMiddleMenu.some((h) => h.value === e.id)
          ),
        },
        Col1: {
          name: 'in-app',
          items: mapDbEntities(dbMiddleMenu, results),
        },
      };
      const bottomMenuObject = {
        Col0: {
          name: 'Components',
          items: [
            {
              id: 'section-label',
              name: componentName('sectionLabel'),
              value: null,
            },
            ...menuOptions,
          ].filter((e) =>
            e.id.includes('specific-') || e.id === 'section-label'
              ? true
              : !dbBottomMenu.some((h) => h.value === e.id)
          ),
        },
        Col1: {
          name: 'in-app',
          items: mapDbEntities(dbBottomMenu, results),
        },
      };
      const mainScreenObject = {
        Col0: {
          name: 'Components',
          items: mainScreenOptions.filter((e) =>
            e.id.includes('specific-')
              ? true
              : !dbMainScreen.some((h) => h.value === e.id)
          ),
        },
        Col1: {
          name: 'in-app',
          items: mapDbEntities(dbMainScreen, results),
        },
      };
      setTopMenu(topMenuObject);
      setMiddleMenu(middleMenuObject);
      setBottomMenu(bottomMenuObject);
      setMainScreen(mainScreenObject);
      setDirty(false);
      setLoadingComponents(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoadingComponents(false);
    }
  };
  useEffect(() => {
    getInitialData();
    // getInitialData is redefined every render
    // eslint-disable-next-line
  }, []);

  // Leaving with unsaved changes (closing the tab; in-app links use Prompt)
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const areaState = {
    mainScreen: [mainScreen, setMainScreen],
    topMenu: [topMenu, setTopMenu],
    middleMenu: [middleMenu, setMiddleMenu],
    bottomMenu: [bottomMenu, setBottomMenu],
  };
  const updateArea = (area, columns) => {
    areaState[area][1](columns);
    setDirty(true);
    setSaved(false);
  };

  // Things to look at before saving; saving is still allowed
  const getChecks = () => {
    const checks = [];
    const areaName = (area) => w(area);
    AREAS.forEach((area) => {
      const items = areaState[area][0].Col1?.items || [];
      // Menus may be left empty; a front page without sections may not
      if (!items.length && area === 'mainScreen') {
        checks.push({ area, text: p('checkEmptyMain') });
      }
      const seenLinks = new Set();
      items.forEach((item, index) => {
        const name = itemTitle(item);
        const where = { name, area: areaName(area) };
        if (hasContent(item.id) && !item.value && item.value !== 0) {
          checks.push({ area, text: p('checkNoContent', where) });
        }
        if (item.id === 'specific-external-link' && item.value) {
          const link = `${item.value}`.trim();
          if (seenLinks.has(link)) {
            checks.push({
              area,
              text: p('checkDuplicateLink', { link, area: areaName(area) }),
            });
          }
          seenLinks.add(link);
        }
        // The website has no widget for it (headings, ForzaSys, Fogis …)
        if (area === 'mainScreen' && !shownOnSite(item.id)) {
          checks.push({ area, text: p('checkNotShown', where) });
        }
        // In the menu panel a heading needs links under it
        const next = items[index + 1];
        if (
          area === 'bottomMenu' &&
          item.id === 'section-label' &&
          (!next || next.id === 'section-label')
        ) {
          checks.push({ area, text: p('checkEmptyHeading', where) });
        }
      });
    });
    return checks;
  };
  const checks = getChecks();

  // The site as club-website draws it, from the current (unsaved) layout:
  // top strip, header (logo, middle menu, ☰), home widgets, footer; the ☰
  // panel holds the bottom menu
  const renderPreview = (activeArea) => {
    const placed = (area) => areaState[area][0].Col1?.items || [];
    const goTo = (area) => setTab(String(AREAS.indexOf(area) + 1));
    const regionClass = (area, className) =>
      `wl-pv__region ${className}${
        area === activeArea ? ' wl-pv__region--active' : ''
      }`;
    const top = placed('topMenu');
    const middle = placed('middleMenu');
    const main = placed('mainScreen');
    // Links before the first heading go under the site's default heading
    const menuSections = [];
    placed('bottomMenu').forEach((item) => {
      if (item.id === 'section-label' || !menuSections.length) {
        menuSections.push({
          key: item.uid,
          heading:
            item.id === 'section-label' ? itemTitle(item) : p('menuSection'),
          links: [],
        });
      }
      if (item.id !== 'section-label') {
        menuSections[menuSections.length - 1].links.push(item);
      }
    });
    // Featured news first: the header lies on top of it
    const overlay = main[0]?.id === 'featuredNews';
    const header = (
      <div
        className={`wl-pv__chrome${overlay ? ' wl-pv__chrome--overlay' : ''}`}
      >
        {top.length > 0 && (
          <button
            type="button"
            className={regionClass('topMenu', 'wl-pv__top')}
            onClick={() => goTo('topMenu')}
            title={w('topMenu')}
          >
            {top.map((item) => (
              <span key={item.uid}>{itemTitle(item)}</span>
            ))}
          </button>
        )}
        <div className="wl-pv__header">
          <span className="wl-pv__logo" />
          <button
            type="button"
            className={regionClass('middleMenu', 'wl-pv__middle')}
            onClick={() => goTo('middleMenu')}
            title={w('middleMenu')}
          >
            {middle.length ? (
              middle.map((item) => (
                <span key={item.uid}>{itemTitle(item)}</span>
              ))
            ) : (
              <span className="wl-pv__none">{w('middleMenu')}</span>
            )}
          </button>
          {menuSections.length > 0 && (
            <button
              type="button"
              className={regionClass('bottomMenu', 'wl-pv__burger')}
              onClick={() => goTo('bottomMenu')}
              title={w('bottomMenu')}
              aria-label={w('bottomMenu')}
            >
              <span />
              <span />
              <span />
            </button>
          )}
        </div>
      </div>
    );
    return (
      <section className="wl-panel wl-panel--preview">
        <div className="wl-panel__head">
          <h2 className="wl-panel__title">{p('preview')}</h2>
          <span className="wl-panel__hint">{p('previewHint')}</span>
        </div>
        <div className="wl-pv-body">
          <div className="wl-pv">
            <div className="wl-pv__browser" aria-hidden="true">
              <span />
              <span />
              <span />
              <span className="wl-pv__address" />
            </div>
            {top.length === 0 && activeArea === 'topMenu' && (
              <p className="wl-pv__note">{p('previewTopHidden')}</p>
            )}
            <div className="wl-pv__page">
              {header}
              <button
                type="button"
                className={regionClass('mainScreen', 'wl-pv__main')}
                onClick={() => goTo('mainScreen')}
                title={w('mainScreen')}
              >
                {main.length ? (
                  main.map((item) => {
                    const type = siteWidgetType(item.id);
                    if (!shownOnSite(item.id)) {
                      return (
                        <span
                          key={item.uid}
                          className="wl-pv__block wl-pv__block--hidden"
                        >
                          {itemTitle(item)} · {p('previewNotShown')}
                        </span>
                      );
                    }
                    return (
                      <span
                        key={item.uid}
                        className={`wl-pv__block${
                          SITE_FULL_WIDTH.includes(item.id)
                            ? ' wl-pv__block--full'
                            : ''
                        }${type === 'featuredNews' ? ' wl-pv__block--hero' : ''}`}
                        style={{ minHeight: PREVIEW_HEIGHTS[type] || 36 }}
                      >
                        {itemTitle(item)}
                      </span>
                    );
                  })
                ) : (
                  <span className="wl-pv__none">{p('previewEmpty')}</span>
                )}
              </button>
              <div className="wl-pv__footer">{p('previewFooter')}</div>
            </div>
          </div>
          {/* The ☰ panel, opened from the header */}
          <div className="wl-pv-menu">
            <div className="wl-pv-menu__title">
              <span className="wl-pv-menu__icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              {p('previewMenuPanel')}
            </div>
            <button
              type="button"
              className={regionClass('bottomMenu', 'wl-pv-menu__body')}
              onClick={() => goTo('bottomMenu')}
              title={w('bottomMenu')}
            >
              {menuSections.length ? (
                menuSections.map((section) => (
                  <span key={section.key} className="wl-pv-menu__section">
                    <span className="wl-pv-menu__heading">
                      {section.heading}
                    </span>
                    {section.links.map((item) => (
                      <span key={item.uid}>{itemTitle(item)}</span>
                    ))}
                  </span>
                ))
              ) : (
                <span className="wl-pv__none">{p('previewNoMenuPanel')}</span>
              )}
            </button>
          </div>
        </div>
      </section>
    );
  };

  const isDisabled = (item) =>
    getPremium(item?.id) ? !getFeatures(features, item?.id) : false;

  // The Available list as shown: filtered by the search, sorted into groups.
  // Drag indexes refer to this order, not to Col0's
  const visibleAvailable = (columns) => {
    const search = query.trim().toLowerCase();
    return [...(columns.Col0?.items || [])]
      .filter(
        (item) => !search || `${item.name || ''}`.toLowerCase().includes(search)
      )
      .sort((x, y) => groupOrder(x.id) - groupOrder(y.id));
  };

  // Puts an available item on the website at `index`; items with content
  // are copied (they can be placed many times) and ask for it right away
  const placeItem = (area, item, index) => {
    const [columns] = areaState[area];
    const placed = { ...item, uid: newUid() };
    const available = hasContent(item.id)
      ? columns.Col0.items
      : columns.Col0.items.filter((entry) => entry !== item);
    const onSite = [...columns.Col1.items];
    onSite.splice(index ?? onSite.length, 0, placed);
    updateArea(area, {
      ...columns,
      Col0: { ...columns.Col0, items: available },
      Col1: { ...columns.Col1, items: onSite },
    });
    if (hasContent(item.id)) {
      setShowModal({ item: placed, type: area, isEdit: false });
    }
  };

  // Takes an item off the website; plain items go back to Available
  const removeItem = (area, item) => {
    const [columns] = areaState[area];
    updateArea(area, {
      ...columns,
      Col0: {
        ...columns.Col0,
        items: hasContent(item.id)
          ? columns.Col0.items
          : [...columns.Col0.items, { id: item.id, name: item.name }],
      },
      Col1: {
        ...columns.Col1,
        items: columns.Col1.items.filter((entry) => entry.uid !== item.uid),
      },
    });
  };

  const onDragEnd = (result, area) => {
    const { source, destination } = result;
    if (!destination) return;
    const [columns] = areaState[area];
    if (source.droppableId === 'Col0') {
      if (destination.droppableId !== 'Col1') return;
      const item = visibleAvailable(columns)[source.index];
      if (item) placeItem(area, item, destination.index);
      return;
    }
    const item = columns.Col1.items[source.index];
    if (destination.droppableId === 'Col0') {
      removeItem(area, item);
      return;
    }
    if (destination.index === source.index) return;
    const onSite = [...columns.Col1.items];
    onSite.splice(source.index, 1);
    onSite.splice(destination.index, 0, item);
    updateArea(area, { ...columns, Col1: { ...columns.Col1, items: onSite } });
  };

  const renderItemIcon = (item) => {
    const IconComponent = getIconComponent(item.id);
    return (
      <span
        className={`wl-item__icon${
          item.id === 'section-label' ? ' wl-item__icon--muted' : ''
        }`}
      >
        <IconComponent size={18} />
      </span>
    );
  };

  const renderAvailable = (area, columns) => {
    const items = visibleAvailable(columns);
    let lastGroup = null;
    return (
      <section className="wl-panel wl-panel--available">
        <div className="wl-panel__head">
          <h2 className="wl-panel__title">
            {s('Available')}
            <span className="form-card__count">
              {columns.Col0.items.length}
            </span>
          </h2>
        </div>
        <div className="wl-panel__search">
          <label className="wl-search">
            <MagnifyIcon size={16} />
            <input
              type="search"
              value={query}
              placeholder={p('searchComponents')}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>
        <Droppable droppableId="Col0">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`wl-list wl-list--available${
                snapshot.isDraggingOver ? ' wl-list--over' : ''
              }`}
            >
              {items.map((item, index) => {
                const group = groupOf(item.id);
                const header =
                  group !== lastGroup ? (
                    <div className="wl-group" key={`group-${group}`}>
                      {p(`group-${group}`)}
                    </div>
                  ) : null;
                lastGroup = group;
                const disabled = isDisabled(item);
                return (
                  <React.Fragment key={`avail-${item.id}`}>
                    {header}
                    <Draggable
                      draggableId={`avail-${item.id}`}
                      index={index}
                      isDragDisabled={disabled}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={`wl-item wl-item--available${
                            disabled ? ' wl-item--disabled' : ''
                          }${dragSnapshot.isDragging ? ' wl-item--dragging' : ''}`}
                        >
                          <DragVerticalIcon
                            size={18}
                            className="wl-item__grip"
                          />
                          {renderItemIcon(item)}
                          <span className="wl-item__text">
                            <span className="wl-item__name">{item.name}</span>
                          </span>
                          {disabled ? (
                            <span
                              className="wl-premium"
                              title={p('premiumHint')}
                            >
                              {p('premium')}
                            </span>
                          ) : (
                            <SquareIconButton
                              icon={<PlusIcon />}
                              title={p('addComponent')}
                              tone="primary"
                              id={`add-${area}-${item.id}`}
                              onClick={() => placeItem(area, item)}
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  </React.Fragment>
                );
              })}
              {provided.placeholder}
              {!items.length && (
                <p className="wl-list__empty">
                  {query ? p('noComponentsMatch') : p('allComponentsUsed')}
                </p>
              )}
            </div>
          )}
        </Droppable>
      </section>
    );
  };

  // The chosen group in the Spotlight modal: what it is, whether anything
  // in it is live, and its tiles (the live ones first)
  const renderSpotlightPicker = (groupId) => {
    if (!groupId) {
      return <p className="wl-pick__empty">{p('spotlightPickHint')}</p>;
    }
    const group = spotlightGroups[groupId];
    if (!group) {
      return null;
    }
    const tiles = group.tiles || [];
    const liveTiles = tiles.filter(isTileLive);
    const shown = [...liveTiles, ...tiles.filter((tile) => !isTileLive(tile))];
    return (
      <div className="wl-pick">
        <dl className="wl-pick__facts">
          <div>
            <dt>{p('spotlightFormat')}</dt>
            <dd>{getReadableFormat(group.format) || '–'}</dd>
          </div>
          <div>
            <dt>{p('spotlightSize')}</dt>
            <dd>{getReadableSize(group.size) || '–'}</dd>
          </div>
          <div>
            <dt>{p('spotlightTiles')}</dt>
            <dd>
              <span
                className={`wl-item__badge${
                  liveTiles.length ? '' : ' wl-item__badge--warning'
                }`}
              >
                {p('spotlightLiveTiles', {
                  live: liveTiles.length,
                  total: tiles.length,
                })}
              </span>
            </dd>
          </div>
          {group.createdAt && (
            <div>
              <dt>{p('spotlightCreatedLabel')}</dt>
              <dd>{moment(group.createdAt).format('D MMM YYYY')}</dd>
            </div>
          )}
        </dl>
        {!liveTiles.length && (
          <p className="wl-pick__warning">{p('spotlightPickNoLive')}</p>
        )}
        {shown.length > 0 && (
          <ul className="wl-pick__tiles">
            {shown.slice(0, 8).map((tile) => (
              <li key={tile.id}>
                <span className="wl-pick__tile-title">{tile.title || '–'}</span>
                <span
                  className={`wl-pick__state${
                    isTileLive(tile) ? ' wl-pick__state--live' : ''
                  }`}
                >
                  {isTileLive(tile) ? p('tileLive') : p('tileNotLive')}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="wl-pick__footer">
          {shown.length > 8 ? (
            <span>{p('spotlightMoreTiles', { count: shown.length - 8 })}</span>
          ) : (
            <span />
          )}
          <Link
            to={`/spotlight/group/edit/${group.id}?tab=2`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {p('editSpotlightGroup')}
          </Link>
        </div>
      </div>
    );
  };

  // Which group a spotlight row shows, its format and how many of its tiles
  // are live, with a link to the group (new tab: this page may have
  // unsaved changes)
  const renderSpotlightDetails = (item) => {
    if (!item.value) {
      return null;
    }
    const group = spotlightGroups[item.value];
    if (!group) {
      return (
        <span className="wl-item__detail wl-item__detail--warning">
          {p('spotlightGroupMissing')}
        </span>
      );
    }
    const total = group.tiles?.length || 0;
    const live = liveTileCount(group.tiles);
    return (
      <>
        <span className="wl-item__detail">
          {[
            group.title !== item.label ? group.title : null,
            getReadableFormat(group.format),
            getReadableSize(group.size),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="wl-item__meta">
          <span
            className={`wl-item__badge${
              live ? '' : ' wl-item__badge--warning'
            }`}
            title={live ? undefined : p('spotlightNoLiveTiles')}
          >
            {p('spotlightLiveTiles', { live, total })}
          </span>
          <Link
            to={`/spotlight/group/edit/${group.id}?tab=2`}
            target="_blank"
            rel="noopener noreferrer"
            className="wl-item__link"
          >
            {p('editSpotlightGroup')}
          </Link>
        </span>
      </>
    );
  };

  const renderOnSite = (area, columns) => (
    <section className="wl-panel wl-panel--site">
      <div className="wl-panel__head">
        <h2 className="wl-panel__title">
          {p('onTheWebsite')}
          <span className="form-card__count">{columns.Col1.items.length}</span>
        </h2>
        <span className="wl-panel__hint">{p('dragToReorder')}</span>
      </div>
      <Droppable droppableId="Col1">
        {(provided, snapshot) => (
          <ol
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`wl-list wl-list--site${
              snapshot.isDraggingOver ? ' wl-list--over' : ''
            }`}
          >
            {columns.Col1.items.map((item, index) => (
              <Draggable key={item.uid} draggableId={item.uid} index={index}>
                {(dragProvided, dragSnapshot) => (
                  <li
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    // The whole row is the grab area; its buttons stay clicks
                    {...dragProvided.dragHandleProps}
                    className={`wl-item wl-item--site${
                      item.id === 'section-label' ? ' wl-item--label' : ''
                    }${dragSnapshot.isDragging ? ' wl-item--dragging' : ''}`}
                  >
                    <span className="wl-item__handle">
                      <DragVerticalIcon size={18} className="wl-item__grip" />
                      <span className="wl-item__position">{index + 1}</span>
                    </span>
                    {renderItemIcon(item)}
                    <span className="wl-item__text">
                      <span className="wl-item__name">{item.name}</span>
                      {item.label !== undefined &&
                        item.label !== null &&
                        item.id !== 'section-label' && (
                          <span className="wl-item__detail">{item.label}</span>
                        )}
                      {item.id === 'section-label' && (
                        <span className="wl-item__detail">{item.label}</span>
                      )}
                      {item.id === 'specific-spotlight' &&
                        renderSpotlightDetails(item)}
                    </span>
                    <span className="icon-actions">
                      {hasContent(item.id) && (
                        <SquareIconButton
                          icon={<EditOutlineIcon />}
                          title={s('edit')}
                          id={`edit-${item.uid}`}
                          onClick={() =>
                            setShowModal({ item, type: area, isEdit: true })
                          }
                        />
                      )}
                      <SquareIconButton
                        icon={<CloseIcon />}
                        title={p('removeComponent')}
                        tone="danger"
                        id={`remove-${item.uid}`}
                        onClick={() => removeItem(area, item)}
                      />
                    </span>
                  </li>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {!columns.Col1.items.length && (
              <li className="wl-list__drop">{p('emptyWebsite')}</li>
            )}
          </ol>
        )}
      </Droppable>
    </section>
  );

  const renderArea = (area) => {
    const [columns] = areaState[area];
    if (!columns.Col1) return null;
    return (
      <>
        <div className="wl-toolbar">
          <p className="wl-intro">{w(`${area}Desc`)}</p>
          {!!getClubWebsite(config) && (
            <a
              href={getClubWebsite(config)}
              target="_blank"
              rel="noopener noreferrer"
              className="wl-view-site"
            >
              <OpenInNewIcon size={16} />
              {p('viewWebsite')}
            </a>
          )}
        </div>
        {showChecks && checks.length > 0 && (
          <div className="wl-checks" role="status">
            <div className="wl-checks__title">
              <AlertOutlineIcon size={18} />
              {p('checksTitle', { count: checks.length })}
            </div>
            <ul>
              {checks.map((check) => (
                <li key={`${check.area}-${check.text}`}>
                  <button
                    type="button"
                    onClick={() =>
                      setTab(String(AREAS.indexOf(check.area) + 1))
                    }
                  >
                    {check.text}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DragDropContext onDragEnd={(result) => onDragEnd(result, area)}>
          <div className="wl-board">
            {renderAvailable(area, columns)}
            {renderOnSite(area, columns)}
          </div>
        </DragDropContext>
        {renderPreview(area)}
      </>
    );
  };

  const handleComponentSave = (payload) => {
    const { item: showModalItem, type } = showModal;
    let selectedLayout = '';
    let selectedMethod = '';
    switch (type) {
      case 'topMenu':
        selectedLayout = topMenu;
        selectedMethod = setTopMenu;
        break;
      case 'middleMenu':
        selectedLayout = middleMenu;
        selectedMethod = setMiddleMenu;
        break;
      case 'bottomMenu':
        selectedLayout = bottomMenu;
        selectedMethod = setBottomMenu;
        break;
      case 'mainScreen':
        selectedLayout = mainScreen;
        selectedMethod = setMainScreen;
        break;
      default:
        return;
    }

    const updatedItems = selectedLayout.Col1.items.map((item) =>
      item.uid === showModalItem.uid ? { ...payload, uid: item.uid } : item
    );
    selectedMethod({
      ...selectedLayout,
      Col1: { ...selectedLayout.Col1, items: updatedItems },
    });
    setDirty(true);
    setSaved(false);
    setShowModal(null);
  };
  const handleModalClosed = () => {
    let selectedLayout = '';
    let selectedMethod = '';
    const { item: showModalItem, type, isEdit } = showModal;
    // Editing a placed item: closing keeps it as it was
    if (isEdit) {
      setShowModal(null);
      return;
    }
    switch (type) {
      case 'topMenu':
        selectedLayout = topMenu;
        selectedMethod = setTopMenu;
        break;
      case 'middleMenu':
        selectedLayout = middleMenu;
        selectedMethod = setMiddleMenu;
        break;
      case 'bottomMenu':
        selectedLayout = bottomMenu;
        selectedMethod = setBottomMenu;
        break;
      case 'mainScreen':
        selectedLayout = mainScreen;
        selectedMethod = setMainScreen;
        break;
      default:
        return;
    }
    // A new item without its content isn't placed after all
    const newLayout = selectedLayout.Col1.items.filter(
      (item) => item.uid !== showModalItem.uid
    );
    selectedMethod({
      ...selectedLayout,
      Col1: { ...selectedLayout.Col1, items: newLayout },
    });
    setShowModal(null);
  };
  return (
    <div>
      <Loading loading={loadingComponents} />
      <CustomWebsiteComponent
        availableEntities={availableEntities}
        renderDetails={{ spotlight: renderSpotlightPicker }}
        showModal={showModal}
        onClose={handleModalClosed}
        selectedComponent={showModal?.item}
        onSave={handleComponentSave}
      />
      <SectionLabelComponent
        showModal={showModal}
        onClose={handleModalClosed}
        selectedComponent={showModal?.item}
        onSave={handleComponentSave}
      />
      <ExternalLinkWebsiteComponent
        showModal={showModal}
        onClose={handleModalClosed}
        selectedComponent={showModal?.item}
        onSave={handleComponentSave}
      />
      <Prompt when={dirty} message={p('leaveWarning')} />
      <Card>
        <CardBody style={{ padding: 0 }}>
          <NavTab
            tabs={AREAS.map((area) => ({
              tab: `${w(area)} (${areaState[area][0].Col1?.items.length || 0})`,
            }))}
            activeTab={tab}
            setTab={setTab}
            size="sm"
          />
          <div className="wl-content">
            {renderArea(AREAS[Number(tab) - 1] || AREAS[0])}
          </div>
          <div className="wl-savebar">
            <span className="wl-savebar__status">
              {dirty && (
                <span className="wl-unsaved">{p('unsavedChanges')}</span>
              )}
              {checks.length > 0 && (
                <button
                  type="button"
                  className="wl-checks-toggle"
                  aria-expanded={showChecks}
                  onClick={() => setShowChecks((value) => !value)}
                >
                  <AlertOutlineIcon size={16} />
                  {p('checksCount', { count: checks.length })}
                </button>
              )}
              {!dirty && saved && (
                <span className="wl-saved">
                  <CheckIcon size={16} />
                  {p('allSaved')}
                </span>
              )}
            </span>
            <span className="wl-savebar__actions">
              {dirty && (
                <Button
                  type="button"
                  outline
                  color="secondary"
                  size="sm"
                  className="mb-0"
                  onClick={getInitialData}
                >
                  {p('discard')}
                </Button>
              )}
              <Button
                type="button"
                color="primary"
                size="sm"
                className="mb-0"
                disabled={!dirty || loadingComponents}
                onClick={() => {
                  // Saving is allowed; the list shows what may be wrong
                  if (checks.length) setShowChecks(true);
                  handleSubmit();
                }}
              >
                {p('saveAll')}
              </Button>
            </span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default WebsiteLayoutPage;
